import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  AppBar, Avatar, Box, Chip, Drawer, IconButton, List, ListItemButton,
  ListItemIcon, ListItemText, Menu, MenuItem, Toolbar, Typography, useMediaQuery,
} from "@mui/material";
import { useState } from "react";
import ApartmentIcon from "@mui/icons-material/Apartment";
import DashboardIcon from "@mui/icons-material/Dashboard";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import HistoryIcon from "@mui/icons-material/History";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import TuneIcon from "@mui/icons-material/Tune";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import TranslateIcon from "@mui/icons-material/Translate";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { useLanguage } from "../i18n/LanguageContext";
import { LANGS } from "../i18n/translations";
import { COLORS } from "../theme";

const DRAWER_WIDTH = 240;

export default function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const { settings } = useSettings();
  const { t, lang, setLang } = useLanguage();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:760px)");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langAnchor, setLangAnchor] = useState(null);

  const navItems = [
    { to: "/panel", label: t("nav.panel"), icon: <DashboardIcon /> },
    { to: "/registrar", label: t("nav.registrar"), icon: <Inventory2Icon /> },
    ...(isAdmin ? [{ to: "/directorio", label: t("nav.directorio"), icon: <PeopleAltIcon /> }] : []),
    { to: "/historial", label: t("nav.historial"), icon: <HistoryIcon /> },
    ...(isAdmin ? [{ to: "/usuarios", label: t("nav.usuarios"), icon: <ManageAccountsIcon /> }] : []),
    ...(isAdmin ? [{ to: "/accesos", label: t("nav.accesos"), icon: <FactCheckIcon /> }] : []),
    ...(isAdmin ? [{ to: "/ajustes", label: t("nav.ajustes"), icon: <TuneIcon /> }] : []),
  ];

  const brandIcon = settings?.logo ? (
    <Avatar variant="rounded" src={settings.logo} sx={{ width: 28, height: 28 }} />
  ) : (
    <ApartmentIcon sx={{ color: COLORS.brass }} />
  );

  const LanguageSwitcher = ({ dark }) => (
    <>
      <IconButton size="small" onClick={(e) => setLangAnchor(e.currentTarget)} title={t("layout.language")} sx={{ color: dark ? "#C7CDD9" : "inherit" }}>
        <TranslateIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={langAnchor} open={!!langAnchor} onClose={() => setLangAnchor(null)}>
        {LANGS.map((l) => (
          <MenuItem
            key={l.code}
            selected={l.code === lang}
            onClick={() => {
              setLang(l.code);
              setLangAnchor(null);
            }}
          >
            {l.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: COLORS.ink, color: "#EDEFE6" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, p: 2.5, pb: 1 }}>
        {brandIcon}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} lineHeight={1.1} noWrap>
            {settings?.condoName || t("common.loading")}
          </Typography>
          <Typography variant="caption" sx={{ color: "#9AA6B8" }}>
            {t("layout.tagline")}
          </Typography>
        </Box>
        <LanguageSwitcher dark />
      </Box>

      <List sx={{ px: 1.5, mt: 1, flex: 1 }}>
        {navItems.map((item) => (
          <ListItemButton
            key={item.to}
            component={NavLink}
            to={item.to}
            onClick={() => setMobileOpen(false)}
            sx={{
              borderRadius: 1.5,
              mb: 0.5,
              color: "#C7CDD9",
              borderLeft: "2px solid transparent",
              "&.active": {
                bgcolor: "rgba(185,129,46,0.16)",
                color: COLORS.brassSoft,
                borderLeftColor: COLORS.brass,
              },
            }}
          >
            <ListItemIcon sx={{ color: "inherit", minWidth: 36 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItemButton>
        ))}
      </List>

      <Box sx={{ p: 2, borderTop: "1px solid rgba(255,255,255,0.12)" }}>
        <Typography variant="body2" fontWeight={600}>
          {user?.name}
        </Typography>
        <Chip
          label={t(`layout.role_${user?.role}`)}
          size="small"
          sx={{ mt: 0.5, mb: 1.5, bgcolor: "rgba(185,129,46,0.16)", color: COLORS.brassSoft, fontSize: 11 }}
        />
        <ListItemButton
          onClick={() => {
            logout();
            navigate("/login");
          }}
          sx={{ borderRadius: 1.5, bgcolor: "rgba(255,255,255,0.08)", color: "#C7CDD9" }}
        >
          <ListItemIcon sx={{ color: "inherit", minWidth: 36 }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t("layout.logout")} primaryTypographyProps={{ fontSize: 13 }} />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: COLORS.paper }}>
      {isMobile && (
        <AppBar position="fixed" sx={{ bgcolor: COLORS.ink }}>
          <Toolbar variant="dense">
            <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(true)}>
              <MenuIcon />
            </IconButton>
            <Typography variant="subtitle1" sx={{ ml: 1, flex: 1 }} noWrap>
              {settings?.condoName || "Recepción"}
            </Typography>
            <LanguageSwitcher />
          </Toolbar>
        </AppBar>
      )}

      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { width: DRAWER_WIDTH, boxSizing: "border-box", border: "none" },
        }}
      >
        {drawerContent}
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 4 }, mt: isMobile ? 6 : 0, maxWidth: 1200 }}>
        <Outlet />
      </Box>
    </Box>
  );
}
